import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Label,
  FormGroup,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import CustomSelect from "components/Common/custom-select";
import CustomDatePicker from "common/CustomDatePicker";
import TableContainer from "components/Common/TableContainer";
import { useFormik } from "formik";
import * as Yup from "yup";
import { exportEmployeeToExcel } from "pages/exports/exportExcel";
import { exportEmployeeToPDF } from "pages/exports/exportPDF";
import { useLocation,useHistory } from "react-router-dom";
import getAccessRights from "components/Common/getAccessRights";
import useButtonAccess from "components/Common/useButtonAccess";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { fetchAttendanceLeaveType, fetchCategories, fetchDepartments, fetchDesignations, fetchEmployeeDropdownAutoList, fetchExtraHoursReport, fetchLeaveReportRequest, getMobilePunchReport, resetAttendanceReport } from "store/actions";
import Loader from "common/pageLoader";
import { getAttendanceSalaryDate } from "common/attendanceSalaryDate";

const typeOptions = [
  { label: "Leave", value: 1 },
  { label: "Permission", value: 2 },
  { label: "On Duty", value: 3 }
];

const statusOptions = [
  { label: "Pending", value: 1},
  { label: "Approved", value: 2 },
  { label: "Rejected", value: 3 },
];

const AttendanceReportLeave = () => {
  const [accessRight, setAccessRight] = useState(null);
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
      const rights = getAccessRights(location.pathname, history);
      setAccessRight(rights?.data);
  }, [location]);
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((prev) => !prev);
  const {
    employeeReport,
    categories,
    departments,
    dropdownAutoList,
    designations,
    leaveTypes,
    loading,
    error,
    success
  } = useSelector((state) => ({
    employeeReport: state.AttendanceReportReducer?.employeeReport ?? [],
    categories: state.CategoryMaster?.categories ?? [],
    departments: state.DepartmentMaster?.departments ?? [],
    dropdownAutoList: state.EmployeeDetailsReducer?.dropdownAutoList ?? [],
    designations: state.DesignationMaster?.designations ?? [],
    leaveTypes: state.AttendanceLeaveManagReducer?.leaveTypes ?? [],

    loading: state.AttendanceReportReducer?.loading,
    error: state.AttendanceReportReducer?.error,
    success: state.AttendanceReportReducer?.success,
  }));

  useEffect(() => {
      if (authDetails) {
        getAttendanceSalaryDate(authDetails.unit_id).then(response => {
          if (response.status === "success" && response.data.length > 0) {
            const data = response.data[0];
            formik.setFieldValue("fromDate", data.from_date.split('/').reverse().join('-'));
            formik.setFieldValue("toDate", data.to_date.split('/').reverse().join('-'));
          }
        });
      }
    }, [authDetails]);

  useEffect(() => {
    dispatch(resetAttendanceReport())
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchCategories(unitId));
    dispatch(fetchDepartments(unitId));
    dispatch(fetchEmployeeDropdownAutoList(unitId))
    dispatch(fetchDesignations(unitId));
    dispatch(fetchAttendanceLeaveType(unitId))
  }, [dispatch]);

  const leaveTypeOptions =
    leaveTypes?.map((item) => ({
      value: item.leavetypeid,
      label: item.leavetype,
      eligible_leave:item.eligible_leave
    })) ?? [];

  const formik = useFormik({
    initialValues: {
      department: "",
      category: "",
      leaveType: "",
      employee: "",
      status: "",
      fromDate: "",
      toDate: "",
      type: "",
      slipNo: "",
    },
    validationSchema: Yup.object({
       type: Yup.number().required("Type is required"),

      leaveType: Yup.mixed().when("type", {
        is: 1, 
        then: Yup.mixed().required("Leave Type is required"),
        otherwise: Yup.mixed().nullable(),
      }),

      fromDate: Yup.date()
        .nullable()
        .required("From Date is required"),

      toDate: Yup.date()
        .nullable()
        .required("To Date is required")
        .min(Yup.ref("fromDate"), "To Date must be greater than or equal to From Date"),
    }),
    onSubmit: (values) => {
      // console.log("Form Values:", values);
      const formData = new FormData();
      formData.append("dept_id", values.department || 0);
      formData.append("cat_id", values.category || 0);
      formData.append("leave_type", values.leaveType?.value || "");
      formData.append("emp_code", values.employee || "");
      formData.append("leave_status", values.status || 0);
      formData.append("from_dt", values.fromDate || "");
      formData.append("to_dt", values.toDate || "");
      formData.append("type", values.type || ""); 
      formData.append("slip_no", values.slipNo || 0); 
      formData.append("create_by", authDetails?.user_id);
      formData.append("unit_id", authDetails?.unit_id);

      dispatch(fetchLeaveReportRequest(formData));

    },
  });

  const getColumnsByType = (type) => {
    return [
      { Header: "S No", Cell: ({ row }) => employeeReport.length - row.index, className: "serial-no text-center", },

      { Header: "Slip#", accessor: "slip_no" || "slip_id" }, 
      { Header: "Code", accessor: "emp_code" },
      { Header: "Name", accessor: "emp_name" },
      { Header: "Category", accessor: "category" },
      { Header: "Department", accessor: "department" },      
      // { Header: "Designation", accessor: "designation" },

      // Type based fields
      ...(type === 1
        ? [
            { Header: "Date", accessor: "date" },
            { Header: "Leave Type", accessor: "leave_type" },
            { Header: "N.Days", accessor: "no_of_days" },
            { Header: "Session", accessor: "session" },
          ]
        : []),

      ...(type === 2
        ? [
            { Header: "Applied Date", accessor: "appiled_dt" },
            { Header: "From Time", accessor: "from_time" },
            { Header: "To Time", accessor: "to_time" },
            { Header: "Duration (Mins)", accessor: "det_mints" },
            { Header: "Permission Type", accessor: "det_from" },
          ]
        : []),

      ...(type === 3
        ? [
            { Header: "Applied Date", accessor: "applied_dt" },
            { Header: "From Time", accessor: "from_time" },
            { Header: "To Time", accessor: "to_time" },
            { Header: "Total Hours", accessor: "total" },
          ]
        : []),

      // Common to all types
      { Header: "Reason", accessor: "reason" },
      { Header: "Status", accessor: "status" },
      { Header: "Approved By", accessor: "approved_by" },
      { Header: "Created By", accessor: "created_by" },
    ];
  };

  const selectedType = formik.values.type;
  const columns = getColumnsByType(Number(selectedType));

  const handleReset = () => {
      formik.resetForm(); 
      dispatch(resetAttendanceReport()); 
  };

  return (
    <div className="page-content">
      <Loader loading={loading}/>
      <Container fluid>
        <Breadcrumbs title="Attendance" breadcrumbItem="Leave" />

        <Card>
          <CardBody>
            <form onSubmit={formik.handleSubmit}>
              <Row className="gy-3">
                <Col md={3}>
                  <Label>Category</Label>
                  <CustomSelect
                    id="category"
                    name="category"
                    options={categories.map(cat => ({ value: cat.empCatgryid, label: cat.empCategory }))}
                    value={
                      categories
                        .map(cat => ({ value: cat.empCatgryid, label: cat.empCategory }))
                        .find(cat => cat.value === formik.values.category) || null
                    }
                    onChange={val => formik.setFieldValue('category', val ? val.value : '')}
                    onBlur={formik.handleBlur}
                    className={`select-dropdown ${formik.touched.category && formik.errors.category ? 'is-invalid' : ''}`}
                  />
                </Col>                

                <Col md={3}>
                  <Label>Department</Label>
                  <CustomSelect
                      id="department"
                      name="department"
                      options={departments.map(dept => ({ value: dept.departmentId, label: dept.departmentname }))}
                      value={
                        departments
                          .map(dept => ({ value: dept.departmentId, label: dept.departmentname }))
                          .find(dept => dept.value === formik.values.department) || null
                      }
                      onChange={val => formik.setFieldValue('department', val ? val.value : '')}
                      onBlur={formik.handleBlur}
                      className={`select-dropdown ${formik.touched.department && formik.errors.department ? 'is-invalid' : ''}`}
                    />
                </Col>
                <Col md={3}>
                  <Label>
                    Employee
                  </Label>
                  <CustomSelect
                      id="employee"
                      name="employee"
                      options={dropdownAutoList.map(emp => ({
                        value: String(emp.emp_id),
                        label: emp.emp_no
                      }))}

                      // UI needs the object, so find matching option
                      value={
                        dropdownAutoList
                          .map(emp => ({
                            value: String(emp.emp_id),
                            label: emp.emp_no
                          }))
                          .find(option => option.value === formik.values.employee) || null
                      }

                      // Form Data: store string only
                      onChange={(selected) =>
                        formik.setFieldValue("employee", selected ? selected.value : "")
                      }
                    />
                    {formik.touched.employee && formik.errors.employee && (
                      <div className="text-danger">
                        {formik.errors.employee}
                      </div>
                    )}
                </Col>
                <Col md={3}>
                  <Label>Type <span className="text-danger">*</span></Label>
                  <CustomSelect
                    name="type"
                    options={typeOptions}
                    value={
                      typeOptions.find(opt => opt.value === formik.values.type) || null
                    }
                    onChange={(val) => {
                      formik.setFieldValue("type", val ? val.value : "");
                      dispatch(resetAttendanceReport());
                      if (val?.value !== 1) {
                        formik.setFieldValue("leaveType", "");
                      }
                    }}
                    onBlur={formik.handleBlur}
                    className={`select-dropdown ${
                      formik.touched.type && formik.errors.type ? "is-invalid" : ""
                    }`}
                  />
                  {formik.touched.type && formik.errors.type && (
                    <div className="text-danger">{formik.errors.type}</div>
                  )}
                </Col>
                {formik.values.type === 1 && (
                  <Col md={3}>
                    <Label>Leave Type <span className="text-danger">*</span></Label>
                    <CustomSelect
                      name="leaveType"
                      value={formik.values.leaveType}
                      onChange={(val) => formik.setFieldValue("leaveType", val)}
                      onBlur={formik.handleBlur}
                      options={leaveTypeOptions}
                    />
                    {formik.touched.leaveType && formik.errors.leaveType && (
                      <span className="text-danger">{formik.errors.leaveType}</span>
                    )}
                  </Col>
                )}
                
                <Col md={3} >
                    <FormGroup>
                    <Label for="fromDate">From Date <span className="text-danger">*</span></Label>
                    <CustomDatePicker
                        name="fromDate"
                        value={formik.values.fromDate || ""}
                        onChange={(date) => formik.setFieldValue("fromDate", date)}
                        onBlur={formik.handleBlur}
                        // disableFuture={true}
                        className={`form-control ${formik.touched.fromDate && formik.errors.fromDate ? "is-invalid" : ""}`}
                    />
                    {formik.touched.fromDate && formik.errors.fromDate && (
                        <div className="text-danger">{formik.errors.fromDate}</div>
                    )}
                    </FormGroup>
                </Col>

                <Col md={3} >
                    <FormGroup>
                    <Label for="toDate">To Date <span className="text-danger">*</span></Label>
                    <CustomDatePicker
                        name="toDate"
                        value={formik.values.toDate || ""}
                        onChange={(date) => formik.setFieldValue("toDate", date)}
                        onBlur={formik.handleBlur}
                        // disableFuture={true}
                        className={`form-control ${formik.touched.toDate && formik.errors.toDate ? "is-invalid" : ""}`}
                    />
                    {formik.touched.toDate && formik.errors.toDate && (
                        <div className="text-danger">{formik.errors.toDate}</div>
                    )}
                    </FormGroup>
                </Col>
                
                <Col md={3}>
                  <Label>Slip #</Label>
                  <input
                    type="text"
                    name="slipNo"
                    value={formik.values.slipNo}
                    onChange={formik.handleChange}
                    className="form-control"
                  />
                </Col>
                <Col md={3}>
                    <Label>Status</Label>
                    <CustomSelect
                      id="leave_status"
                      name="leave_status"
                      options={statusOptions.map(stat => ({ value: stat.value, label: stat.label }))} 
                      value={
                        statusOptions
                          .map(stat => ({ value: stat.value, label: stat.label }))
                          .find(stat => stat.value === formik.values.status) || null
                      }
                      onChange={val => formik.setFieldValue("status", val ? val.value : "")}
                      onBlur={formik.handleBlur}                      
                    />
                    {formik.touched.status && formik.errors.status && (
                      <div className="text-danger">{formik.errors.status}</div>
                    )}
                  </Col>

                <Col className="d-flex mob-center align-items-center justify-content-end">
                  <Button type="submit" className="btn button-search me-2 ">
                    <i className="fas fa-search me-1" /> Search
                  </Button>
                  <Button type="button" className="btn button-cancel me-2" onClick={handleReset}>
                    <i className="fas fa-sync me-1" /> Cancel
                  </Button>
                  {employeeReport && employeeReport.length > 0 && (
                    <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="down">
                      <DropdownToggle color="primary" className="btn button-export">
                        <i className="fas fa-file-export me-1" /> Export
                      </DropdownToggle>

                      <DropdownMenu>
                        <DropdownItem onClick={() => exportEmployeeToExcel(employeeReport, authDetails?.unit_fullname, authDetails?.unit_address, "Leave Report")}>
                          <i className="fas fa-file-excel me-2 text-success" /> Export to Excel
                        </DropdownItem>

                        <DropdownItem onClick={() => exportEmployeeToPDF(employeeReport, authDetails?.unit_fullname, authDetails?.unit_address, "Leave Report", columns)}>
                          <i className="fas fa-file-pdf me-2 text-danger" /> Export to PDF
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  )}  
                </Col>
              </Row>
            </form>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <TableContainer
              columns={columns}
              data={employeeReport}
              isGlobalFilter={true}
              customPageSize={10}
              tableSorter={true}
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default AttendanceReportLeave;
